import { useState, useRef, useEffect, useMemo } from 'react'
import SearchInput from './common/SearchInput'
import HighlightText from './common/HighlightText'
import TokenBadge from './common/TokenBadge'
import { filterByText } from '../utils/filterByText'

// Semantic Views 좌측 사이드바.
// 협업 모드 list_views 응답에 owner_user_id / is_mine / owner_name 메타가 포함되어
// 항목별로 작성자 + "나" 배지 + 설명 + 마지막 수정일을 노출한다.
// list-item 의 시각 디자인은 Explore 페이지(.explore-view-item)와 동일한
// 공통 패턴(.std-list-item) 을 사용한다.
// KBNL-141(리뷰): selected 는 owner::name 복합 키. onSelect(name, ownerUserId),
// onRename(oldName, newName, ownerUserId) 로 owner-aware 처리(동명이인 충돌 방지).
export default function Sidebar({ views, selected, onSelect, onNew, onRename, onDuplicate }) {
  const [editingKey, setEditingKey] = useState(null)   // owner::name
  const [editValue, setEditValue] = useState('')
  const [query, setQuery] = useState('')
  const inputRef = useRef(null)

  useEffect(() => {
    if (editingKey && inputRef.current) {
      inputRef.current.focus()
      inputRef.current.select()
    }
  }, [editingKey])

  // 본인 뷰만 표시되는 영역이라 owner 필터 불필요. name + description + owner_name 검색.
  const filteredViews = useMemo(() => {
    return filterByText(views || [], query, ['name', 'description', 'owner_name'])
  }, [views, query])

  const startRename = (v, isMine) => {
    if (!isMine) return  // 협업 모드: 남의 항목은 인라인 rename 금지
    setEditingKey(`${v.owner_user_id || ''}::${v.name}`)
    setEditValue(v.name)
  }

  const commitRename = () => {
    const newName = editValue.trim()
    if (!editingKey) { setEditingKey(null); return }
    const sep = editingKey.indexOf('::')
    const oldOwner = sep >= 0 ? editingKey.slice(0, sep) : ''
    const oldName = sep >= 0 ? editingKey.slice(sep + 2) : editingKey
    if (newName && newName !== oldName && onRename) {
      onRename(oldName, newName, oldOwner)
    }
    setEditingKey(null)
  }

  const cancelRename = () => {
    setEditingKey(null)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commitRename()
    else if (e.key === 'Escape') cancelRename()
  }

  return (
    <aside className="sidebar">
      <div className="sidebar-header">Semantic Views</div>
      <button className="btn-new" onClick={onNew}>
        <span>+</span> 새 Semantic View
      </button>
      {/* 검색 bar — name + description + owner (Ctrl+K 로 포커스) */}
      <div className="sidebar-search">
        <SearchInput
          value={query}
          onChange={setQuery}
          placeholder="뷰 검색 (Ctrl+K)"
          total={(views || []).length}
          matched={filteredViews.length}
          compact
        />
      </div>
      <div className="std-list">
        {views.length === 0 && (
          <div className="std-list-empty">저장된 뷰 없음</div>
        )}
        {views.length > 0 && filteredViews.length === 0 && (
          <div className="std-list-empty">조건에 맞는 뷰 없음</div>
        )}
        {filteredViews.map(v => {
          // owner 메타 (협업 모드일 때만 채워짐)
          const ownerName = v.owner_name || v.owner_user_id || ''
          const isMine = v.is_mine !== false  // 메타가 없으면(개인 모드) 본인 것으로 취급
          const tooltip = ownerName
            ? [
                `작성자: ${ownerName}`,
                v.owner_user_id ? `사번: ${v.owner_user_id}` : null,
                isMine ? '(더블클릭으로 이름 변경)' : null,
              ].filter(Boolean).join('\n')
            : (isMine ? '더블클릭으로 이름 변경' : undefined)
          // KBNL-141(리뷰): owner::name 복합 키로 선택/리네임 판정 (동명이인 충돌 방지).
          const vKey = `${v.owner_user_id || ''}::${v.name}`
          const isEditing = editingKey === vKey
          return (
            <div
              key={vKey}
              className={`std-list-item ${selected === vKey ? 'active' : ''}`}
              onClick={() => { if (!isEditing) onSelect(v.name, v.owner_user_id || '') }}
              onDoubleClick={() => startRename(v, isMine)}
              title={tooltip}
            >
              <div className="std-list-name-row">
                {isEditing ? (
                  <input
                    ref={inputRef}
                    className="std-list-rename-input"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={handleKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    onDoubleClick={(e) => e.stopPropagation()}
                  />
                ) : (
                  <span className="std-list-name">
                    <HighlightText text={v.name} query={query} />
                  </span>
                )}
                {ownerName && !isEditing && (
                  <span className="std-list-owner" title={tooltip}>
                    <HighlightText text={ownerName} query={query} />
                    {isMine && <span className="std-list-user-badge">나</span>}
                  </span>
                )}
                {/* 본인 항목에만 노출되는 hover 액션 — 복제 (다이얼로그에서 새 이름 확정) */}
                {isMine && !isEditing && onDuplicate && (
                  <button
                    className="std-list-action-btn"
                    title="복제"
                    onClick={(e) => { e.stopPropagation(); onDuplicate(v) }}
                  >⎘</button>
                )}
              </div>
              {!isEditing && v.description && (
                <div className="std-list-desc">
                  <HighlightText text={v.description} query={query} />
                </div>
              )}
              {!isEditing && (v.updated_at || v.token_count != null) && (
                <div className="std-list-meta">
                  {v.updated_at && <span>{String(v.updated_at).slice(0, 10)}</span>}
                  <TokenBadge count={v.token_count} />
                </div>
              )}
            </div>
          )
        })}
      </div>
    </aside>
  )
}
